/**
 * Flight Schedule Accessor Utilities
 *
 * The campaign requestor codebase has a historical naming drift where:
 * - GraphQL returns `flightSchedules` (plural)
 * - AntD form state uses `flightSchedule` (singular)
 *
 * These utilities normalize access so components don't need to remember the
 * `obj.flightSchedule || obj.flightSchedules || []` incantation everywhere.
 *
 * Long-term goal: migrate ALL form state to `flightSchedules` (plural) to match
 * the GraphQL schema, then these utilities become pass-throughs.
 */

/**
 * Read flight schedules from an object, normalizing both naming variants.
 *
 * @param {Object} obj – campaign, lineItem, or any object that may contain flight schedules
 * @returns {Array} flight schedules array (never null/undefined)
 */
export const getFlightSchedules = (obj) => {
  if (!obj) return [];
  return obj.flightSchedule || obj.flightSchedules || [];
};

/**
 * Check whether an object has any flight schedules.
 *
 * @param {Object} obj
 * @returns {boolean}
 */
export const hasFlightSchedules = (obj) => {
  return getFlightSchedules(obj).length > 0;
};

/**
 * Write flight schedules to an object, setting the canonical form field name
 * and cleaning up the stale GraphQL name if present.
 *
 * @param {Object} obj – object to mutate
 * @param {Array} value – flight schedules array
 * @returns {Object} the mutated object (for chaining)
 */
export const setFlightSchedules = (obj, value) => {
  if (!obj) return obj;
  obj.flightSchedule = value;
  delete obj.flightSchedules;
  return obj;
};

/**
 * Copy flight schedules from a source object to a target object.
 * Useful during cloning operations.
 *
 * @param {Object} source
 * @param {Object} target
 */
export const copyFlightSchedules = (source, target) => {
  if (!source || !target) return;
  const schedules = getFlightSchedules(source);
  setFlightSchedules(target, schedules.map((flight) => ({ ...flight })));
};

/**
 * Reset flight schedules on an object (used when clearing or initializing).
 *
 * @param {Object} obj
 */
export const resetFlightSchedules = (obj) => {
  setFlightSchedules(obj, []);
};
